'use client'

import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { Marque } from './Marque'
import { creuser } from './choregraphie'
import { ARRIVEE_MS, EFFACEMENT_DELAI_MS, EFFACEMENT_MS, RETRAIT_MS } from './fondu'

/**
 * Le poinçon : la seule chose centrée de toute la page.
 *
 * ⭐ LE TRIANGLE EN APLAT EST PEINT LE PREMIER, ET IL RESTE. Ce n'est pas un
 * écran d'attente, c'est l'état par défaut de la page : la scène en volume vient
 * se poser PAR-DESSUS, en différé, et seulement si la machine peut la tenir.
 * Dans l'ordre inverse — canvas d'abord, repli ensuite — la page passe par un
 * trou noir de plusieurs centaines de millisecondes sur la seule zone qu'on
 * regarde, et c'est exactement ce qu'on voit sur les pages qui ratent leur
 * effet.
 *
 * ⛔ Le canvas n'est JAMAIS sur le chemin critique. Il est chargé à la demande
 * (`ssr: false`), après l'entrée dans l'écran, et pendant un temps mort du fil
 * principal. Le texte du certificat doit être lisible avant que la moindre ligne
 * de trois dimensions ne soit téléchargée.
 */

/**
 * ⭐⭐ LE TÉLÉCHARGEMENT PART AVANT LE MONTAGE, ET C'EST LA MOITIÉ DE L'ATTENTE.
 * Le montage est volontairement différé jusqu'à un temps mort du fil principal
 * (`requestIdleCallback`, jusqu'à 1,5 s), et c'est LUI qui déclenchait le
 * téléchargement du morceau three.js. Les deux délais s'additionnaient donc au
 * lieu de se recouvrir : 🟢 constaté le 2026-09-09, deux à cinq secondes d'aplat
 * avant que l'objet n'arrive.
 *
 * ⭐ Le geste est de séparer les deux : on demande le morceau DÈS QUE la machine
 * est déclarée capable, et on ne monte qu'au temps mort. Le réseau travaille
 * pendant l'attente au lieu de commencer après elle. Rien d'autre ne change,
 * et surtout pas le fait que le canvas ne soit jamais sur le chemin critique.
 *
 * ⚠️ Une seule promesse gardée en module : deux appels ne déclenchent pas deux
 * téléchargements, et le `dynamic` réutilise exactement celle-là.
 */
let chargement: Promise<typeof import('./Scene')> | null = null
const charger = () => (chargement ??= import('./Scene'))

const Scene = dynamic(() => charger().then((m) => m.Scene), { ssr: false })

export function Poincon({ police, titre, numero }: { police: string; titre: string; numero: string }) {
  const cadre = useRef<HTMLDivElement>(null)
  const [monte, setMonte] = useState(false)
  const [pret, setPret] = useState(false)
  const [imagePerdue, setImagePerdue] = useState(false)
  const gravee = useRef(false)

  /**
   * ⛔⛔ LA GRAVURE SE CREUSE QUAND LA SCÈNE ARRIVE, PAS QUAND LA PAGE S'OUVRE.
   * Elle était jouée par la ligne de temps d'ouverture, à t ≈ 0,95 s. Or le
   * canvas est différé et son morceau pèse : il se monte deux à cinq secondes
   * plus tard. 🟢 Mesuré le 2026-09-09 : l'animation se jouait donc dans le
   * vide, et l'objet apparaissait déjà gravé. Elle ne se voyait QUE sur une
   * machine assez rapide pour monter la scène avant la fin de l'ouverture, ce
   * qui explique le « pas à chaque fois » : c'était une course, pas un réglage.
   *
   * ⭐ C'est le même défaut que l'observateur jamais appelé, par une autre
   * porte : un effet joué là où personne ne peut le voir ne lève aucune erreur.
   * Le déclencheur doit être l'ÉVÈNEMENT VISIBLE, jamais une horloge partie
   * avant lui.
   *
   * ⚠️ Une seule fois. `onPret` est aussi rappelé à chaque restauration du
   * contexte WebGL, et une gravure qui se recreuse à chaque incident du pilote
   * graphique cesse d'être un évènement.
   */
  const apparaitre = () => {
    setPret(true)
    if (gravee.current) return
    gravee.current = true
    creuser()
  }

  useEffect(() => {
    const el = cadre.current
    if (!el || !capable()) return

    /* Le morceau part maintenant ; le montage attendra le temps mort. */
    void charger()

    /* ⚠️ La proximité est MESURÉE, pas observée. 🟢 Mesuré le 2026-09-07 : un
       `IntersectionObserver` posé sur ce cadre n'a JAMAIS rendu la main dans un
       navigateur dont le rendu est piloté à la demande — pas d'erreur, pas de
       retard, simplement aucun appel. Le poinçon restait en aplat pour toujours,
       et rien nulle part ne le signalait. C'est le défaut le plus cher de la
       page : son sujet, absent en silence.
       ⭐ Une lecture géométrique ne peut pas ne pas répondre. */
    const proche = () => {
      const r = el.getBoundingClientRect()
      return r.top < window.innerHeight * 2 && r.bottom > -window.innerHeight
    }

    let jete = false
    const lancer = () => {
      if (jete) return
      /* ⚠️ `requestIdleCallback` plutôt qu'un montage direct : le canvas compile
         des programmes graphiques, et cette compilation bloque le fil principal.
         Lancée pendant que la page finit de se peindre, elle se voit comme un
         accroc. Le délai de repli garantit qu'elle finit par partir même sur une
         page qui ne se repose jamais. */
      if ('requestIdleCallback' in window) window.requestIdleCallback(() => setMonte(true), { timeout: 1500 })
      else setTimeout(() => setMonte(true), 300)
    }

    if (proche()) {
      lancer()
      return () => {
        jete = true
      }
    }

    const surDefilement = () => {
      if (!proche()) return
      window.removeEventListener('scroll', surDefilement)
      window.removeEventListener('resize', surDefilement)
      lancer()
    }
    window.addEventListener('scroll', surDefilement, { passive: true })
    window.addEventListener('resize', surDefilement, { passive: true })
    return () => {
      jete = true
      window.removeEventListener('scroll', surDefilement)
      window.removeEventListener('resize', surDefilement)
    }
  }, [])

  return (
    <>
    {/* ⛔ La hauteur SUIT la largeur, elle n'est pas posée par palier : une
        hauteur fixe sur un écran étroit donne un cadre bien plus haut que large,
        le cadrage recule la caméra pour faire tenir le socle, et l'objet se
        retrouve minuscule entre deux bandes vides. */}
    <div ref={cadre} className="poincon-cadre relative mt-10 h-[clamp(300px,58vw,520px)]">
    <div
      id="poincon"
      /* ⚠️ Le marqueur de repli vit ICI, sur le parent, et non sur `<Marque>` :
         TypeScript accepte en silence un attribut a tiret sur un composant, et
         le composant ne le transmet pas. L'attribut n'atteignait donc jamais le
         document, sans la moindre erreur nulle part. */
      data-repli={imagePerdue ? '' : undefined}
      className="plaque flex items-center justify-center overflow-hidden"
    >
      {/* ⛔⛔ LE DERNIER REPLI : le triangle en aplat, en masque CSS. Il ne
          demande ni réseau ni accélération graphique. Une zone vide serait le
          pire résultat possible sur la seule chose que la page a à montrer.
          ⚠️ Mais il n'apparaît QUE si l'image échoue : le poinçon a un fond
          transparent, donc laissé dessous en permanence, l'aplat violet
          transparaîtrait à travers le bloc de verre et doublerait la marque. */}
      {/* ⚠️ Il est TOUJOURS dans le document, jamais conditionnel au rendu : il
          sert aussi de version imprimable, et une page qu'on imprime ne
          re-execute pas le composant. Le CSS decide quand il se voit. */}
      <Marque quoi="triangle" largeur={190} className="poincon-trait" style={{ color: 'var(--violet)' }} />

      {/* ⭐⭐ LE REPLI EST UNE IMAGE DE LA SCÈNE, RENDUE PAR LA SCÈNE
          (`scripts/poster-poincon.mjs`). Un dessin « qui ressemble » diverge dès
          la première retouche de matériau, et personne ne s'en aperçoit : celui
          qui n'a pas d'accélération graphique voit alors un autre objet que tout
          le monde, sans que rien ne le signale.

          ⚠️ Elle sert AUSSI de première image à ceux qui ont la 3D : elle est
          identique à ce que la scène affichera, donc le passage de l'une à
          l'autre ne se voit pas. C'est ce qui permet de différer le canvas sans
          que la zone ne clignote.

          ⚠️ WebP et non AVIF : 🟢 vérifié le 2026-09-07, `toDataURL('image/avif')`
          rend du PNG, sans erreur ni avertissement. Le navigateur décode l'AVIF,
          il ne l'encode pas. */}
      {/* ⭐⭐ LE FONDU EST SÉQUENCÉ, PAS CROISÉ, ET LES DURÉES VIENNENT DE
          `fondu.ts`, qui porte la démonstration et les trois mesures. En deux
          phrases : le poster tient sa PLEINE opacité tant que la scène n'a pas
          atteint la sienne, donc la couverture du cadre vaut 1 des deux côtés
          du passage, quelle que soit la courbe d'accélération du navigateur.

          ⛔ ET AU RETOUR IL EST REPOSÉ, PAS FONDU. 🟢 Mesuré le 2026-09-09 : le
          faire arriver en fondu pendant que la scène s'efface creusait la
          couverture à 65,7 %, soit PIRE que le fondu croisé symétrique que tout
          ce dispositif existe pour éviter. Un contexte WebGL perdu est une
          toile vide : son remplaçant n'a rien à faire arriver doucement. */}
      {/* ⚠️ `<img>` NATIF, ET C'EST VOULU. `next/image` demanderait un chargeur
          d'images, que `output: 'export'` n'a pas sans `unoptimized` : le kit
          promet de marcher en export statique. Il chargerait aussi en différé
          par défaut, alors que ce poster est la PREMIÈRE image de la page.
          ⛔ La règle `no-img-element` reste active partout ailleurs : on la coupe
          sur ces deux lignes précises, jamais sur le dossier. 🟢 Le 2026-09-11,
          NG-2026-001 devait la couper sur tout le kit pour construire. */}
      {/* eslint-disable-next-line @next/next/no-img-element -- natif voulu, voir au-dessus */}
      <img
        src="/marque/poincon.webp"
        alt=""
        aria-hidden
        decoding="async"
        onError={() => setImagePerdue(true)}
        className="pointer-events-none absolute inset-0 h-full w-full object-contain"
        style={{
          opacity: pret || imagePerdue ? 0 : 1,
          transition: pret
            ? `opacity ${EFFACEMENT_MS}ms ease-out ${EFFACEMENT_DELAI_MS}ms`
            : 'none',
        }}
      />

      {monte && (
        <div
          className="absolute inset-0"
          style={{
            opacity: pret ? 1 : 0,
            transition: `opacity ${pret ? ARRIVEE_MS : RETRAIT_MS}ms ease-out`,
          }}
        >
          <Scene
            police={police}
            titre={titre}
            numero={numero}
            onPret={apparaitre}
            /* ⛔ Le poster revient quand le contexte WebGL est perdu. Sans lui
               le cadre serait VIDE, et un bloc de verre vide est le seul etat
               que cette page n'a pas le droit de montrer. */
            onPerdu={() => setPret(false)}
          />
        </div>
      )}
    </div>
    </div>

    {/* ⭐⭐ LE CACHET EST UN SECOND ÉLÉMENT, PAS LE PREMIER DÉPLACÉ. Faire
        voyager le poinçon jusqu'au coin le redimensionnait à chaque image, donc
        le canvas réallouait ses cibles de rendu, et sa transformation entrait en
        concurrence avec celle de l'animation d'ouverture, sur la même propriété.
        Deux éléments qui apparaissent et disparaissent n'ont aucun de ces trois
        problèmes : rien ne bouge, rien ne se redimensionne, rien ne se dispute.

        ⭐ Et il n'a pas besoin d'être de la 3D vivante. C'est l'image rendue
        depuis la scène, identique au pixel près, donc le passage ne se voit pas.
        À cent quarante pixels, personne ne distingue un rendu d'un rendu ; et la
        scène du haut est de toute façon à l'arrêt une fois hors de l'écran. Une
        seconde scène WebGL aurait coûté un contexte graphique, une carte
        d'environnement et deux cibles de réfraction, pour un timbre.

        ⚠️ Masqué sous 1024 px : la marge droite n'existe pas sur un téléphone,
        le cachet viendrait se poser sur le texte. */}
    <div className="cachet" aria-hidden>
      {/* eslint-disable-next-line @next/next/no-img-element -- natif voulu, voir le poster */}
      <img src="/marque/poincon.webp" alt="" decoding="async" />
    </div>
    </>
  )
}

/**
 * ⛔ CE QUI DÉCIDE, C'EST LA MACHINE, PAS LA TAILLE DE L'ÉCRAN. Une largeur de
 * fenêtre ne dit rien de la puissance : un téléphone récent tient la scène sans
 * broncher, un vieux portable de bureau non. On lit donc les trois choses que le
 * navigateur accepte de dire, et on s'abstient au moindre doute — un objet qui
 * saccade est pire qu'un aplat qui tient.
 *
 * ⚠️ `saveData` d'abord : quand quelqu'un a demandé qu'on économise ses données,
 * lui envoyer une scène en volume est un refus déguisé de sa demande.
 */
function capable(): boolean {
  const n = navigator as Navigator & {
    connection?: { saveData?: boolean }
    deviceMemory?: number
  }
  if (n.connection?.saveData) return false
  if (typeof n.deviceMemory === 'number' && n.deviceMemory < 4) return false
  if (typeof n.hardwareConcurrency === 'number' && n.hardwareConcurrency < 4) return false
  try {
    /* ⚠️ Le contexte est demandé POUR DE VRAI, puis rendu. Se fier à la présence
       de `WebGLRenderingContext` dans la fenêtre ne prouve rien : il est déclaré
       même quand le pilote est sur liste noire, et le canvas rend alors un carré
       vide sans lever la moindre erreur. */
    const essai = document.createElement('canvas')
    const ctx = essai.getContext('webgl2') ?? essai.getContext('webgl')
    if (!ctx) return false
    ;(ctx as WebGLRenderingContext).getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}
